/**
 * EmailDraftDialog
 *
 * Dialog for composing the email/fax body that accompanies the LOR.
 * Encapsulates email template selection, body editing, and police report
 * attachment logic.
 */
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { blobsApiClient } from '@/services/api/blobService';
import type { Blob as MatterBlob } from '@/services/types/blob-types';
import type { LorDeliveryMethod } from '@/services/types/document-types';

import type { LorEmailTemplateKey } from './lorDocUtils';

interface EmailDraftDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    templateKey: LorEmailTemplateKey;
    body: string;
    policeReportId: string | null;
    policeReportFilename: string | null;
  }) => Promise<void>;
  deliveryMethod: LorDeliveryMethod;
  primaryClientName: string;
  matterId: string;
  isSubmitting: boolean;
}

const EMAIL_TEMPLATE_OPTIONS: {
  key: LorEmailTemplateKey;
  label: string;
  description: string;
}[] = [
  {
    key: 'LOR_WITHOUT_PR',
    label: 'LoR only',
    description: 'Send the letter of representation by itself.',
  },
  {
    key: 'LOR_WITH_PR',
    label: 'LoR + police report',
    description: 'Send the letter along with a police report attachment.',
  },
];

const buildEmailTemplateBody = (
  template: LorEmailTemplateKey,
  primaryClientName: string,
): string => {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Good morning,' : 'Good afternoon,';
  const intro =
    template === 'LOR_WITH_PR'
      ? 'Please see the attached letter of representation and police report for our client'
      : 'Please see the attached letter of representation for our client';
  return `${salutation}

${intro}, ${primaryClientName}.

Please provide our office with a copy of the acknowledgment letter and requested policy documents at your earliest convenience.

Should you have any questions or concerns please do not hesitate to reach out.

Thank you, and have a wonderful day.`;
};

export const EmailDraftDialog: React.FC<EmailDraftDialogProps> = ({
  open,
  onClose,
  onSubmit,
  deliveryMethod,
  primaryClientName,
  matterId,
  isSubmitting,
}) => {
  const [emailTemplateKey, setEmailTemplateKey] =
    React.useState<LorEmailTemplateKey>('LOR_WITHOUT_PR');
  const [emailBodyDraft, setEmailBodyDraft] = React.useState('');
  const [selectedPoliceReport, setSelectedPoliceReport] = React.useState<{
    id: string;
    filename: string;
  } | null>(null);

  const { data: matterBlobs = [], isFetching: isMatterBlobsLoading } =
    blobsApiClient.useGetBlobsQuery(matterId, {
      skip: !matterId || !open,
    });

  const matterBlobOptions = React.useMemo(
    () =>
      (matterBlobs as MatterBlob[]).map((blob) => ({
        id: String(blob.id),
        filename:
          blob.name ||
          (blob as { original_filename?: string }).original_filename ||
          'File',
      })),
    [matterBlobs],
  );

  // Initialize body when dialog opens
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpen.current) {
      setEmailTemplateKey('LOR_WITHOUT_PR');
      setEmailBodyDraft(
        buildEmailTemplateBody('LOR_WITHOUT_PR', primaryClientName),
      );
      setSelectedPoliceReport(null);
    }
    prevOpen.current = open;
  }, [open, primaryClientName]);

  const requiresPoliceReport = emailTemplateKey === 'LOR_WITH_PR';

  const canSubmit =
    Boolean(emailBodyDraft.trim()) &&
    (!requiresPoliceReport || Boolean(selectedPoliceReport));

  const handleSelectEmailTemplate = (value: LorEmailTemplateKey) => {
    setEmailTemplateKey(value);
    setEmailBodyDraft(buildEmailTemplateBody(value, primaryClientName));
    if (value === 'LOR_WITHOUT_PR') {
      setSelectedPoliceReport(null);
    }
  };

  const handleSelectPoliceReport = (blobId: string) => {
    if (!blobId) {
      setSelectedPoliceReport(null);
      return;
    }
    const match = matterBlobOptions.find((file) => file.id === blobId);
    if (!match) {
      setSelectedPoliceReport(null);
      return;
    }
    setSelectedPoliceReport({ id: match.id, filename: match.filename });
  };

  const handleSubmit = async () => {
    await onSubmit({
      templateKey: emailTemplateKey,
      body: emailBodyDraft.trim(),
      policeReportId: selectedPoliceReport?.id ?? null,
      policeReportFilename: selectedPoliceReport?.filename ?? null,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Draft insurer email</DialogTitle>
          <DialogDescription>
            Customize the email body that will be used when we send the LoR
            after review is complete.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <div className="space-y-2">
            <p className="text-sm font-medium text-zinc-700">Template</p>
            <RadioGroup
              value={emailTemplateKey}
              onValueChange={(value) =>
                handleSelectEmailTemplate(value as LorEmailTemplateKey)
              }
              className="grid gap-3 sm:grid-cols-2"
            >
              {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  htmlFor={`email-template-${option.key}`}
                  className={`cursor-pointer rounded-2xl border p-3 text-left shadow-sm transition ${
                    emailTemplateKey === option.key
                      ? 'border-blue-500 bg-blue-50/70 text-blue-900'
                      : 'border-zinc-200 bg-white text-zinc-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem
                      id={`email-template-${option.key}`}
                      value={option.key}
                    />
                    <div>
                      <p className="text-sm font-semibold">{option.label}</p>
                      <p className="text-xs text-zinc-500">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lor-email-body">
              {deliveryMethod === 'fax'
                ? 'Fax Cover Sheet Message'
                : 'Email Body'}
            </Label>
            <Textarea
              id="lor-email-body"
              rows={6}
              value={emailBodyDraft}
              onChange={(event) => setEmailBodyDraft(event.target.value)}
            />
          </div>
          {requiresPoliceReport && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-zinc-700">
                Police report attachment
              </p>
              {isMatterBlobsLoading ? (
                <p className="text-sm text-zinc-500">
                  Loading matter files…
                </p>
              ) : matterBlobOptions.length > 0 ? (
                <RadioGroup
                  value={selectedPoliceReport?.id ?? ''}
                  onValueChange={handleSelectPoliceReport}
                  className="space-y-2 max-h-[240px] overflow-y-auto p-1"
                >
                  {matterBlobOptions.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2"
                    >
                      <RadioGroupItem
                        id={`police-report-${file.id}`}
                        value={file.id}
                      />
                      <label
                        htmlFor={`police-report-${file.id}`}
                        className="cursor-pointer text-sm font-medium text-zinc-800"
                      >
                        {file.filename}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-200 px-3 py-2 text-sm text-zinc-500">
                  Upload a police report to this matter to attach it here.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Save & Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
